import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	CalendarDaysIcon,
	MagnifyingGlassIcon,
	ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Alert, AppLoader } from '../../components/ui';
import { staffProfileApi, ApiError } from '../../api';
import type {
	StaffBooking,
	StaffBookingsResponse,
	StaffBookingTab,
} from '../../types/staffBookings';
import BookingDetailModal from './BookingDetailModal';

// Tab configuration
const TABS: { key: StaffBookingTab; label: string }[] = [
	{ key: 'today', label: 'Today' },
	{ key: 'upcoming', label: 'Upcoming' },
	{ key: 'completed', label: 'Completed' },
	{ key: 'cancelled', label: 'Cancelled' },
];

// Task badge color mapping (from reference HTML)
const TASK_BADGE_STYLES: Record<string, string> = {
	Liner: 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-200',
	Picker: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-200',
	Dropper: 'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-200',
	Feeder: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200',
	Whole: 'bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white',
};

// Status-based left border colors
const STATUS_BORDER_COLORS: Record<string, string> = {
	CONFIRMED: 'border-l-emerald-500',
	PENDING: 'border-l-amber-500',
	IN_PROGRESS: 'border-l-blue-500',
	COMPLETED: 'border-l-emerald-500',
	CANCELLED: 'border-l-red-500',
};

// Format currency
const formatCurrency = (amount: number): string => {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
};

// Format time from appointmentDateTime string
const formatTime = (dateTimeStr: string): { time: string; period: string } => {
	try {
		const date = new Date(dateTimeStr);
		const hours = date.getHours();
		const minutes = date.getMinutes();
		const period = hours >= 12 ? 'PM' : 'AM';
		const displayHours = hours % 12 || 12;
		const displayMinutes = minutes.toString().padStart(2, '0');
		return { time: `${displayHours}:${displayMinutes}`, period };
	} catch {
		return { time: '-', period: '' };
	}
};

// Format duration from minutes
const formatDuration = (minutes: number): string => {
	if (!minutes) return '';
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	if (hours > 0 && mins > 0) return `${hours}h ${mins.toString().padStart(2, '0')}m`;
	if (hours > 0) return `${hours}h 00m`;
	return `${mins}m`;
};

const StaffBookings = () => {
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState<StaffBookingTab>('today');
	const [bookings, setBookings] = useState<StaffBooking[]>([]);
	const [tabCounts, setTabCounts] = useState({
		today: 0,
		upcoming: 0,
		completed: 0,
		cancelled: 0,
	});
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [selectedDate, setSelectedDate] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);

	// Debounce search input
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
		}, 300);
		return () => clearTimeout(timer);
	}, [search]);

	// Fetch bookings
	const fetchBookings = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const response: StaffBookingsResponse = await staffProfileApi.getBookings({
				tab: activeTab,
				search: debouncedSearch || undefined,
				date: activeTab !== 'today' && selectedDate ? selectedDate : undefined,
			});

			setBookings(response.bookings);
			setTabCounts({
				today: response.todayCount,
				upcoming: response.upcomingCount,
				completed: response.completedCount,
				cancelled: response.cancelledCount,
			});
		} catch (err) {
			const apiError = err as ApiError;
			setError(apiError.message || 'Failed to load bookings');
		} finally {
			setIsLoading(false);
		}
	}, [activeTab, debouncedSearch, selectedDate]);

	useEffect(() => {
		fetchBookings();
	}, [fetchBookings]);

	// Get border color for booking card based on status
	const getBorderColor = (status: string): string => {
		return STATUS_BORDER_COLORS[status] || 'border-l-gray-300';
	};

	// Get opacity class for completed/cancelled
	const getCardOpacity = (status: string): string => {
		if (status === 'COMPLETED') return 'opacity-85';
		if (status === 'CANCELLED') return 'opacity-70';
		return '';
	};

	// Render action section based on active tab
	const renderActions = (booking: StaffBooking) => {
		if (activeTab === 'today') {
			return (
				<button
					onClick={e => {
						e.stopPropagation();
						navigate(`/staff/tcr/new?appointmentId=${booking.id}`);
					}}
					className="px-4 py-2 rounded-lg text-sm font-medium text-white cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg"
					style={{
						background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
					}}
				>
					Complete
				</button>
			);
		}

		if (activeTab === 'completed') {
			return (
				<span className="px-3.5 py-1.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
					&#10003; Completed
				</span>
			);
		}

		if (activeTab === 'cancelled') {
			return (
				<span className="px-3.5 py-1.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
					&#10007; Cancelled
				</span>
			);
		}

		return null;
	};

	// Render booking card
	const renderBookingCard = (booking: StaffBooking) => {
		const { time, period } = formatTime(booking.appointmentDateTime);
		const duration = formatDuration(booking.durationMinutes);
		const bookingDate = new Date(booking.appointmentDateTime).toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
		});
		const serviceDetails = [booking.serviceName, booking.size, booking.hairLength]
			.filter(Boolean)
			.join(' \u2022 ');

		return (
			<div
				key={booking.id}
				onClick={() => setSelectedBookingId(booking.id)}
				className={`bg-white dark:bg-gray-700/50 rounded-xl p-5 shadow-sm flex items-center gap-5 transition-all hover:translate-x-1 hover:shadow-md border-l-4 cursor-pointer flex-wrap md:flex-nowrap ${getBorderColor(booking.status)} ${getCardOpacity(booking.status)}`}
			>
				{/* Time Block */}
				<div className="text-center min-w-[80px] p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
					<div className="text-xs font-medium text-gray-500 dark:text-gray-400">
						{bookingDate}
					</div>
					<div className="text-lg font-bold text-gray-900 dark:text-white whitespace-nowrap">
						{time} {period}
					</div>
					{duration && <div className="text-xs text-[#667eea] mt-1">{duration}</div>}
				</div>

				{/* Info Section */}
				<div className="flex-1 min-w-0">
					<div className="text-base font-semibold text-gray-900 dark:text-white mb-0.5">
						{booking.customerName || 'Unknown'}
					</div>
					<div className="text-xs text-gray-400 dark:text-gray-500 mb-1">
						{booking.providerBusinessName}
					</div>
					<div className="text-sm text-[#667eea] font-medium mb-1">
						{serviceDetails || '-'}
					</div>

					{/* Task Badges */}
					{booking.assignedTasks && booking.assignedTasks.length > 0 && (
						<div className="flex items-center gap-1.5 mt-2 flex-wrap">
							{booking.assignedTasks.map(taskType => (
								<span
									key={taskType}
									className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold ${TASK_BADGE_STYLES[taskType] || 'bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400'}`}
								>
									{taskType}
								</span>
							))}
						</div>
					)}
				</div>

				{/* Amount + Actions */}
				<div className="flex flex-row items-center justify-between md:flex-col md:items-end gap-2 w-full md:w-auto mt-3 md:mt-0 pt-3 md:pt-0 border-t md:border-t-0 border-gray-200 dark:border-gray-600">
					<div className="text-lg font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
						{formatCurrency(booking.amount)}
					</div>
					{renderActions(booking)}
				</div>
			</div>
		);
	};

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900 dark:text-white">Bookings</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					View and manage your scheduled Bookings
				</p>
			</div>

			{/* Filter Bar */}
			<div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm mb-5 flex gap-3 items-center flex-wrap">
				<div className="flex-1 min-w-[200px] relative">
					<MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
					<input
						type="text"
						value={search}
						onChange={e => setSearch(e.target.value)}
						placeholder="Search by client name or service..."
						className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors"
					/>
				</div>
				<input
					type="date"
					value={activeTab === 'today' ? '' : selectedDate}
					onChange={e => setSelectedDate(e.target.value)}
					disabled={activeTab === 'today'}
					className={`px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors ${activeTab === 'today' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
				/>
			</div>

			{/* Tabs */}
			<div className="flex gap-2 mb-5 overflow-x-auto">
				{TABS.map(tab => (
					<button
						key={tab.key}
						onClick={() => {
							setActiveTab(tab.key);
							if (tab.key === 'today') setSelectedDate('');
						}}
						className={`px-6 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${
							activeTab === tab.key
								? 'text-white border-transparent'
								: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-[#667eea] hover:text-[#667eea]'
						}`}
						style={
							activeTab === tab.key
								? {
										background:
											'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
									}
								: undefined
						}
					>
						{tab.label}
						{tabCounts[tab.key] > 0 && (
							<span
								className={`ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold ${
									activeTab === tab.key
										? 'bg-white/20 text-white'
										: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
								}`}
							>
								{tabCounts[tab.key]}
							</span>
						)}
					</button>
				))}
			</div>

			{/* Error Alert */}
			{error && (
				<Alert
					type="warning"
					icon={<ExclamationTriangleIcon className="w-8 h-8" />}
					title="Unable to Load Bookings"
					message={error}
					actions={[{ label: 'Retry', onClick: fetchBookings, primary: true }]}
					onDismiss={() => setError(null)}
				/>
			)}

			{/* Loading State */}
			{isLoading && (
				<div className="py-12">
					<AppLoader message="Loading bookings" variant="inline" />
				</div>
			)}

			{/* Bookings List */}
			{!isLoading && !error && (
				<div className="flex flex-col gap-3">
					{bookings.length === 0 ? (
						<div className="text-center py-16">
							<CalendarDaysIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
							<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
								No bookings found
							</h3>
							<p className="text-sm text-gray-500 dark:text-gray-400">
								{debouncedSearch || selectedDate
									? 'Try adjusting your search or date filter'
									: `No ${activeTab} bookings to show`}
							</p>
						</div>
					) : (
						bookings.map(booking => renderBookingCard(booking))
					)}
				</div>
			)}
			{/* Booking Detail Modal */}
			<BookingDetailModal
				bookingId={selectedBookingId}
				onClose={() => setSelectedBookingId(null)}
			/>
		</AppLayout>
	);
};

export default StaffBookings;
